import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import BackButton from 'js/social/shared/BackButton';
import useInfiniteScroll from 'js/utils/hooks/useInfiniteScroll';
import News from 'js/social/News/News';
import CoreXNews from 'js/social/CoreX/News/News';
import { Colors } from 'js/design_system/Color';
import { Skeleton } from 'js/design_system/skeleton/Skeleton';
import CheckIcon from 'js/design_system/icons/check.svg';
import routes from 'sitewide-js/core_pre_webpack/routes';
import styled from 'astroturf/react';
import { FeedContext } from '../contexts/feed';
import { SuggestionsContext } from '../contexts/suggestions';
import useNews from './hooks/useNews';
import useUsers from './hooks/useUsers';
import { SuggestionsCarousel } from 'js/social/suggestions/SuggestionsCarousel';
import { PodcastLibraryAd1 } from 'js/social/PodcastLibraryAd1';
import { PodcastLibraryAd2 } from 'js/social/PodcastLibraryAd2';
import { PodcastLibraryAd3 } from 'js/social/PodcastLibraryAd3';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import {
  getImpressionPage,
  getSocialImpressionSource,
} from 'js/social/ImpressionHelper';
import ViewsWatcher from '../shared/ViewsWatcher';
import * as arbitraryEvents from 'js/core_webpack/arbitrary_events';
import { NewsType, RawScoreData } from '../shared/types';
import toSnakecaseKeys from 'js/utils/toSnakecaseKeys';
import { FeedbackModalWatcher } from 'js/social/shared/FeedbackModalWatcher';
import { isRecommendation } from 'js/social/shared/utils';
import _FeedContainer from "Feed-FeedContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/Feed/Feed.tsx?styleId=FeedContainer";
import _CaughtUpContainer from "Feed-CaughtUpContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/Feed/Feed.tsx?styleId=CaughtUpContainer";


const SCROLL_BUFFER = 2400;
type userActions = ReturnType<typeof useUsers>;
type newsActions = ReturnType<typeof useNews>;

const FeedContainer = /*#__PURE__*/styled("div", null, {
  displayName: "FeedContainer",
  styles: _FeedContainer
});

const CaughtUpContainer = /*#__PURE__*/styled("div", null, {
  displayName: "CaughtUpContainer",
  styles: _CaughtUpContainer
});

type Props = {
  personName?: string | undefined;
  filter?: string;
  className?: string;
  emptyState?: React.ReactNode;
  hasError: boolean;
  trackInteractions?: boolean;
  showReasons?: boolean;
  hideActionsOnPaperRecs?: boolean;
  showFeedbackModal?: boolean;
  insert?: () => void;
  injectInFeed?: React.ReactNode | undefined;
  recommendedPodcasts?: Array<{
    id: number;
    workId: number;
    workTitle: string;
    workAuthors: string;
    workThumbnailUrl: string | null;
  }>;
  podcastLibraryAdsEnabled?: boolean;
};

const ViewClickIntersectionWatcher: React.FC<
  React.PropsWithChildren<{
    type: NewsType;
    id: number | string;
    onClick: () => void;
    onView: () => Promise<void>;
    onIntersection: () => void;
  }>
> = ({ type, id, onClick, onView, onIntersection, children }) => {
  const container = useRef<HTMLDivElement | null>(null);
  const intersectionObserver = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const cleanup = () => {
      intersectionObserver.current?.disconnect();
    };

    const handleIntersection = (entries: IntersectionObserverEntry[]): void => {
      if (entries[0].isIntersecting) {
        onIntersection();
        intersectionObserver.current?.disconnect();
      }
    };

    if (container.current == null) return cleanup;

    const newIntersectionObserver = new IntersectionObserver(
      handleIntersection,
      {
        threshold: 0.9,
      },
    );
    newIntersectionObserver.observe(container.current);
    intersectionObserver.current = newIntersectionObserver;

    return cleanup;
  }, [container, intersectionObserver, onIntersection]);

  return (
    <ViewsWatcher
      type={type}
      id={id}
      sendView={onView}
      threshold={0.8}
      keyPrefix="vciw"
    >
      <div onClick={onClick} ref={container}>
        {children}
      </div>
    </ViewsWatcher>
  );
};

type SeenItems = {
  [key: string]: RawScoreData;
};

const Feed: React.FC<Props & userActions & newsActions> = ({
  className,
  emptyState,
  news,
  filter,
  enableInfiniteScroll,
  isLoading,
  hasNextPage,
  fetchNextPage,
  checkRepeatNews,
  checkRepeatPage,
  refreshFeed,
  showReasons = true,
  trackInteractions = false,
  showFeedbackModal,
  injectInFeed,
  recommendedPodcasts,
  podcastLibraryAdsEnabled = false,
  ...actions
}) => {
  const { isLoading: suggestionsLoading, getRandomGroupId } =
    useContext(SuggestionsContext);
  const {
    feedConfig: { page },
    inActivationOverhaul,
    showDenseDesktop,
  } = useContext(FeedContext);
  const NewsComponent = inActivationOverhaul ? CoreXNews : News;
  const impressionPage = getImpressionPage(page);
  const shouldTrackImpressions = ['home', 'research_interest'].includes(page);
  const refreshFeedRef = useRef(refreshFeed);
  refreshFeedRef.current = refreshFeed;
  const feedContainerRef = useRef<HTMLDivElement>(null);

  const seenItems = useRef<SeenItems>({});

  // Shuffle podcast ads deterministically per session
  const shuffledPodcastAds = useMemo(() => {
    if (!podcastLibraryAdsEnabled) {
      return [];
    }

    const availableAds = [
      <PodcastLibraryAd1 key="podcast-ad1" />,
      <PodcastLibraryAd2 key="podcast-ad2" />,
    ];
    if (recommendedPodcasts && recommendedPodcasts.length >= 1) {
      availableAds.push(
        <PodcastLibraryAd3 key="podcast-ad3" podcasts={recommendedPodcasts} />,
      );
    }

    // Shuffle using seeded random for deterministic ordering
    const shuffledAds = availableAds
      .map((item) => ({ sortKey: Math.random(), value: item }))
      .sort((a, b) => a.sortKey - b.sortKey)
      .map((obj) => obj.value);

    return shuffledAds;
  }, [podcastLibraryAdsEnabled, recommendedPodcasts]);

  const seenItemsData = (positiveKey: string, action: 'view' | 'click') => {
    const positiveItem = seenItems.current[positiveKey];
    const negativeItems = Object.fromEntries(
      Object.entries(seenItems.current).filter(
        ([key, _scoreData]) => key !== positiveKey,
      ),
    );

    return {
      version_number: 1,
      interaction_type: action,
      positive: toSnakecaseKeys(positiveItem),
      negative: JSON.stringify(
        Object.keys(negativeItems).map((key) => {
          return toSnakecaseKeys(negativeItems[key]);
        }),
      ),
    };
  };

  useInfiniteScroll(
    document,
    enableInfiniteScroll,
    fetchNextPage,
    true,
    SCROLL_BUFFER,
  );

  useEffect(() => {
    refreshFeedRef.current();
  }, [filter]);

  useEffect(() => {
    checkRepeatNews(news);
    checkRepeatPage(news);
  }, [checkRepeatPage, checkRepeatNews, news]);

  const feed = useMemo(() => {
    const feedComponents: ReactNode[] = [];
    let seenRecommendation = false;
    let appliedRecommendation = false;
    let adjustedIndex = 0;
    let adIndex = 0; // Track which ad to insert next

    for (let i = 0; i < news.length; i++) {
      const n = news[i];
      const key = `${n.newsType}|${n.newsId}`;

      const trackInteractionsForComponent =
        trackInteractions &&
        n.newsType !== 'openalex_work' &&
        // Hoisted works won't have filled out raw score data, so don't try to
        // track them here
        n.rawScoreData &&
        Object.keys(n.rawScoreData).length > 0;

      feedComponents.push(
        trackInteractionsForComponent ? (
          <ViewClickIntersectionWatcher
            key={`vciw-${n.newsType}-${n.newsId}`}
            type={n.newsType}
            id={n.newsId}
            onIntersection={() => {
              seenItems.current[key] = n.rawScoreData;
            }}
            onView={() => {
              seenItems.current[key] = n.rawScoreData;
              arbitraryEvents.write(
                'NewsFeedItemInteraction',
                seenItemsData(key, 'view'),
              );
              return Promise.resolve();
            }}
            onClick={() => {
              seenItems.current[key] = n.rawScoreData;
              arbitraryEvents.write(
                'NewsFeedItemInteraction',
                seenItemsData(key, 'click'),
              );
            }}
          >
            <NewsComponent
              key={key}
              index={adjustedIndex}
              showReason={showReasons}
              {...n}
              {...actions}
            />
          </ViewClickIntersectionWatcher>
        ) : (
          <NewsComponent
            key={key}
            index={adjustedIndex}
            showReason={showReasons}
            {...n}
            {...actions}
          />
        ),
      );
      adjustedIndex += 1;

      if (injectInFeed && adjustedIndex === 2) {
        feedComponents.push(injectInFeed);
        adjustedIndex += 1;
      }

      // Insert podcast library ads at positions 4, 9, 14
      const adPositions = [4, 9, 14];
      if (
        adIndex < shuffledPodcastAds.length &&
        adIndex < adPositions.length &&
        adjustedIndex === adPositions[adIndex]
      ) {
        feedComponents.push(shuffledPodcastAds[adIndex]);
        adjustedIndex += 1;
        adIndex += 1;
      }

      if (isRecommendation(n)) {
        seenRecommendation = true;
      }
      if (
        showFeedbackModal &&
        adjustedIndex >= 6 &&
        seenRecommendation &&
        !appliedRecommendation
      ) {
        appliedRecommendation = true;
        feedComponents.push(
          <FeedbackModalWatcher key="feedback-modal-watcher" />,
        );
      }

      if (page === 'home' && adjustedIndex === 7) {
        if (suggestionsLoading) {
          // Short-circuit until we know whether or not to show the suggestions carousel
          feedComponents.push(
            <Skeleton key="suggestions-skeleton" type="news-feed" />,
          );
          break;
        } else if (getRandomGroupId(4)) {
          feedComponents.push(
            <SuggestionsCarousel
              key="suggestions-carousel"
              index={adjustedIndex}
            />,
          );
          adjustedIndex += 1;
        }
      }
    }

    return feedComponents;
  }, [
    page,
    news,
    actions,
    getRandomGroupId,
    suggestionsLoading,
    seenItems,
    trackInteractions,
    showReasons,
    showFeedbackModal,
    injectInFeed,
    NewsComponent,
    shuffledPodcastAds,
  ]);

  return (
    <div className={className}>
      {page === 'single_post' && (
        <BackButton href={routes.home_url()}>Home</BackButton>
      )}
      {!isLoading && news.length === 0 && emptyState}

      {feed.length > 0 && (
        <FeedContainer
          ref={feedContainerRef}
          spaced={!['single_post', 'research_interest'].includes(page)}
          overhaul={!!inActivationOverhaul}
          variant={showDenseDesktop ? 'dense' : 'default'}
        >
          {feed}
        </FeedContainer>
      )}

      {isLoading && (
        <ImpressionWatcher
          active={shouldTrackImpressions}
          source={getSocialImpressionSource(impressionPage, 'skeleton')}
        >
          <Skeleton type={page === 'single_post' ? 'news' : 'news-feed'} />
        </ImpressionWatcher>
      )}
      {page !== 'single_post' &&
        !isLoading &&
        !hasNextPage &&
        news.length > 0 && (
          <ImpressionWatcher
            active={shouldTrackImpressions}
            source={getSocialImpressionSource(
              impressionPage,
              `feed-${feed.length}`,
              'eof',
            )}
          >
            <CaughtUpContainer>
              <CheckIcon color={Colors.Green} />
              You're all caught up!
            </CaughtUpContainer>
          </ImpressionWatcher>
        )}
    </div>
  );
};

export default Feed;
